/*****************************************************
 * Packages
 ******************************************************/
import kebabcase from 'lodash.kebabcase';
import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/*****************************************************
 * Components
 ******************************************************/
import Input from '../Input';
import Button from '../other-components/Button';
import { default as InputLabel } from '../Label';
import { PagesWrap, Icon, Label, PageBody } from './Pages.sc';
import Accordion, {
    AccordionItem,
    AccordionDetails,
    AccordionSummary,
} from '../other-components/Accordion';

/*****************************************************
 * Actions & Selectors
 ******************************************************/
import * as actions from '../../redux/actions';
import { getIsLoading } from '../../redux/selectors/site';

/*****************************************************
 * Pages Component
 ******************************************************/
function Pages({
    pages,
    siteURL,
    onEditPage,
    onRemovePage,
    onAddNewPage,
    onSelectPage,
    duplicatePage,
    editPageIndex,
}) {
    const params = useParams();
    const history = useHistory();
    const dispatch = useDispatch();
    const [isNew, setNew] = useState(false);
    const isLoading = useSelector(getIsLoading);
    const { siteId } = params;

    const handleNameChange = useCallback(
        (e, index) => {
            const { value } = e.target;
            const slug = kebabcase(value);
            const page = pages[index];
            const nextPage = {
                ...page,
                name: value,
                slug: isNew ? slug : page.slug,
            };
            onEditPage(nextPage, index);
        },
        [isNew, onEditPage, pages]
    );

    const handleSlugChange = useCallback(
        (e, index) => {
            const { value } = e.target;
            const page = pages[index];
            const data = { ...page, slug: value };
            onEditPage(data, index);
        },
        [onEditPage, pages]
    );

    const handleOnBlur = useCallback((index) => {
        setNew(false);
    }, []);

    const handleRemovePage = useCallback(
        (index) => {
            const page = pages[index];
            if (page.id) {
                const ok = window.confirm(
                    'Are you sure you want to remove the page?'
                );
                if (!ok) return;
                dispatch(
                    actions.removePage({
                        siteId,
                        pageId: page.id,
                        onSuccess: () =>
                            editPageIndex === index &&
                            history.push(`/builder/site/${siteId}`),
                    })
                );
            } else {
                onRemovePage(index);
            }
        },
        [dispatch, editPageIndex, history, onRemovePage, pages, siteId]
    );

    const handleAddClick = useCallback(() => {
        setNew(true);
        onAddNewPage({ slug: '', name: 'New Page' });
    }, [onAddNewPage]);

    const handleClickPage = useCallback(
        (index) => {
            onSelectPage(index);
            const page = pages[index];
            const siteUrl = siteId
                ? `/builder/site/${siteId}/page`
                : '/builder';
            const pageUrl =
                siteId && page.id ? `${siteUrl}/${page.id}` : siteUrl;
            history.push(pageUrl);
        },
        [history, onSelectPage, pages, siteId]
    );

    const createOrUpdatePage = useCallback(
        (index) => {
            let page = { ...pages[index] };
            delete page.type;
            delete page.isModified;
            page = { ...page, data: page.data && JSON.stringify(page.data) };
            dispatch(actions.createOrUpdatePage({ page, siteId, history }));
        },
        [dispatch, history, pages, siteId]
    );

    return (
        <PagesWrap>
            <strong>Regular Pages</strong>
            <Accordion
                size="sm"
                type="box"
                toggleable
                onlyIconToggle
                expendedIcon="chevron-up"
                collapsedIcon="chevron-down"
            >
                {pages?.map((page, index) => (
                    <AccordionItem key={index}>
                        <AccordionSummary>
                            <Label
                                active={index === editPageIndex}
                                onClick={() => handleClickPage(index)}
                            >
                                {page.name}
                            </Label>
                            <Icon
                                className="icon"
                                onClick={() => duplicatePage(index)}
                            >
                                <FontAwesomeIcon icon={['far', 'clone']} />
                            </Icon>
                            {page.type !== 'INDEX' && (
                                <Icon
                                    className="icon"
                                    onClick={() => handleRemovePage(index)}
                                >
                                    <FontAwesomeIcon
                                        icon={['far', 'trash-alt']}
                                    />
                                </Icon>
                            )}
                        </AccordionSummary>
                        <AccordionDetails>
                            <PageBody>
                                {page.type !== 'INDEX' && (
                                    <>
                                        <InputLabel size="sm">
                                            Page Name
                                        </InputLabel>
                                        <Input
                                            size="sm"
                                            placeholder="Page Name"
                                            value={page.name}
                                            disabled={page.type === 'INDEX'}
                                            onBlur={() => handleOnBlur(index)}
                                            onChange={(e) =>
                                                handleNameChange(e, index)
                                            }
                                        />

                                        <InputLabel size="sm">Slug</InputLabel>
                                        <Input
                                            size="sm"
                                            value={page.slug}
                                            placeholder="example-new-page"
                                            onBlur={() => handleOnBlur(index)}
                                            onChange={(e) =>
                                                handleSlugChange(e, index)
                                            }
                                        />
                                        {siteURL && (
                                            <span
                                                style={{
                                                    marginTop: 5,
                                                    wordBreak: 'break-all',
                                                    display: 'inline-block',
                                                }}
                                            >
                                                <span>https://{siteURL}/</span>
                                                <strong>
                                                    {page.slug}
                                                </strong>{' '}
                                                <a
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    href={`https://${siteURL}/${page.slug}`}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={[
                                                            'fal',
                                                            'external-link',
                                                        ]}
                                                    />
                                                </a>
                                            </span>
                                        )}
                                        {siteId && (
                                            <div style={{ marginTop: 20 }}>
                                                <Button
                                                    disabled={isLoading}
                                                    onClick={() =>
                                                        createOrUpdatePage(
                                                            index
                                                        )
                                                    }
                                                    size="sm"
                                                >
                                                    {isLoading
                                                        ? 'Saving...'
                                                        : 'Save'}
                                                    {isLoading && (
                                                        <FontAwesomeIcon
                                                            spin
                                                            icon={[
                                                                'far',
                                                                'spinner',
                                                            ]}
                                                        />
                                                    )}
                                                </Button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </PageBody>
                        </AccordionDetails>
                    </AccordionItem>
                ))}
            </Accordion>

            <div style={{ marginTop: 15 }}>
                <Button
                    size="sm"
                    width="100%"
                    border="dotted"
                    onClick={handleAddClick}
                >
                    + Add New Page
                </Button>
            </div>
        </PagesWrap>
    );
}

export default React.memo(Pages);
